/**
 * Pure Plotly chart builders for the MARS Performance section.
 *
 * No React — these turn a `PerformancePeriodData` into ready-to-render
 * `{ data, layout }` pairs. `PerformanceSection.tsx` composes them.
 *
 * Layouts here intentionally only carry the chart-specific diffs (titles,
 * gridlines, axis labels, reference shapes). The black axis line + inner
 * ticks come from `DEFAULT_LAYOUT` via `mergeLayout`, which `PlotlyChart`
 * always applies — see `lib/plotly/defaults.ts`.
 */
import type { Data, Layout } from 'plotly.js';

import { AXIS_GRID } from '@/lib/plotly/defaults';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { ASSET_COLORS, FULCRUM_COLORS } from '@/lib/plotly/colors';

import type { PerformancePeriodData } from '../types';

/**
 * Timeseries-family margin. The Tactical / Drawdown sub-charts want the
 * family margin but NOT the title/legend/inner-tick convention (they render
 * their own title + hide the legend), so they spread this directly instead
 * of calling `chartLayout`.
 */
const TIMESERIES_MARGIN: Partial<Layout['margin']> = { l: 60, r: 20, t: 50, b: 50, pad: 4 };

/** Canonical ordering for asset class display. */
export const ASSET_ORDER = ['Equities', 'Bonds', 'Commodities', 'Inflation'] as const;

export interface BuiltPlot {
  data: Data[];
  layout: Partial<Layout>;
}

export interface AssetClassBuiltPlots {
  tactical: BuiltPlot;
  cumulativeNav: BuiltPlot;
}

export function buildPerformancePlots(
  data: PerformancePeriodData | undefined,
  hasBenchmark: boolean,
): { cumulative: BuiltPlot; tactical: BuiltPlot; drawdown: BuiltPlot } {
  const empty: BuiltPlot = { data: [], layout: {} };
  if (!data?.timeseries) return { cumulative: empty, tactical: empty, drawdown: empty };

  const ts = data.timeseries;

  // Benchmark NAV: index 0 is the base (100); each later index compounds
  // the previous NAV by (1 + return/100).
  const benchmarkNav: number[] = [];
  const benchmarkReturns = ts.benchmark_returns ?? [];
  for (let i = 0; i < benchmarkReturns.length; i++) {
    benchmarkNav.push(i === 0 ? 100 : benchmarkNav[i - 1] * (1 + benchmarkReturns[i] / 100));
  }

  const cumulativeData: Data[] = [
    {
      type: 'scatter',
      mode: 'lines',
      name: 'Portfolio',
      x: ts.dates,
      y: ts.cumulative_nav,
      line: { color: FULCRUM_COLORS.peacockBlue, width: 2.5 },
      hovertemplate: hoverTemplate(),
    },
  ];
  if (hasBenchmark && benchmarkNav.length > 0) {
    cumulativeData.push({
      type: 'scatter',
      mode: 'lines',
      name: 'Benchmark',
      x: ts.dates,
      y: benchmarkNav,
      line: { color: FULCRUM_COLORS.lightTeal, width: 2.5 },
      hovertemplate: hoverTemplate(),
    });
  }

  const cumulative: BuiltPlot = {
    data: cumulativeData,
    layout: {
      // Convention: title at top, legend BETWEEN title and chart drawing
      // area. Pass `showLegend: hasBenchmark` so absolute-only funds still
      // hide the legend.
      ...chartLayout({
        family: 'timeseries',
        showLegend: hasBenchmark,
        marginOverride: { t: 90, b: 70, l: 56, r: 24 },
      }),
      title: { text: cumulativeTitle(data, hasBenchmark) },
      yaxis: {
        title: { text: 'NAV' },
        ...AXIS_GRID,
      },
      xaxis: {
        type: 'date',
        ...AXIS_GRID,
      },
    },
  };

  const tactical: BuiltPlot = hasBenchmark
    ? {
        data: [
          {
            type: 'scatter',
            mode: 'lines',
            name: 'Tactical',
            x: ts.dates,
            y: ts.active_nav,
            line: { color: FULCRUM_COLORS.lightTeal, width: 2 },
            hovertemplate: hoverTemplate(),
          },
        ],
        layout: {
          margin: TIMESERIES_MARGIN,
          title: { text: 'Tactical Portion (Portfolio - Benchmark)' },
          showlegend: false,
          yaxis: {
            title: { text: 'Active NAV (Base 100)' },
            ...AXIS_GRID,
          },
          xaxis: {
            type: 'date',
            ...AXIS_GRID,
          },
          shapes: [
            {
              type: 'line',
              xref: 'paper',
              x0: 0,
              x1: 1,
              y0: 100,
              y1: 100,
              line: { color: '#666', width: 1, dash: 'dash' },
            },
          ],
        },
      }
    : empty;

  const drawdown: BuiltPlot = hasBenchmark
    ? {
        data: [
          {
            type: 'scatter',
            mode: 'lines',
            name: 'Drawdown',
            x: ts.dates,
            y: ts.drawdowns,
            line: { color: FULCRUM_COLORS.coral, width: 2 },
            hovertemplate: hoverTemplate({ suffix: '%' }),
          },
        ],
        layout: {
          margin: TIMESERIES_MARGIN,
          title: { text: 'Drawdown' },
          showlegend: false,
          yaxis: {
            title: { text: 'Drawdown (%)' },
            ...AXIS_GRID,
          },
          xaxis: {
            type: 'date',
            ...AXIS_GRID,
          },
          // Zero reference line.
          shapes: [
            {
              type: 'line',
              xref: 'paper',
              x0: 0,
              x1: 1,
              y0: 0,
              y1: 0,
              line: { color: '#666', width: 1, dash: 'dash' },
            },
          ],
        },
      }
    : empty;

  return { cumulative, tactical, drawdown };
}

/**
 * Build the two asset-class charts shown in the "Asset Class Breakdown" section.
 *
 *   1. Tactical Performance (Active NAV) -- one line per asset, only for
 *      funds with a benchmark.
 *   2. Asset Class Cumulative NAV -- one line per asset.
 */
export function buildAssetClassPlots(
  data: PerformancePeriodData | undefined,
  hasBenchmark: boolean,
): AssetClassBuiltPlots {
  const empty: BuiltPlot = { data: [], layout: {} };
  if (!data?.asset_classes || Object.keys(data.asset_classes).length === 0) {
    return { tactical: empty, cumulativeNav: empty };
  }

  const ac = data.asset_classes;

  // 1. Tactical Performance (Active NAV by asset)
  const tacticalTraces: Data[] = [];
  if (hasBenchmark) {
    ASSET_ORDER.forEach((assetClass) => {
      const d = ac[assetClass];
      if (!d?.timeseries?.dates || !d.timeseries.active_nav) return;
      const color = ASSET_COLORS[assetClass] ?? '#666';
      tacticalTraces.push({
        type: 'scatter',
        mode: 'lines',
        name: assetClass,
        x: d.timeseries.dates,
        y: d.timeseries.active_nav,
        line: { color, width: 2 },
        hovertemplate: hoverTemplate({ name: assetClass }),
      });
    });
  }

  const tactical: BuiltPlot =
    tacticalTraces.length > 0
      ? {
          data: tacticalTraces,
          layout: {
            // Convention: title at top, legend BETWEEN title and chart.
            ...chartLayout({
              family: 'timeseries',
              marginOverride: { t: 90, b: 70, l: 56, r: 24 },
            }),
            title: { text: 'Tactical Performance (Active NAV)' },
            yaxis: {
              title: { text: 'Active NAV (Base 100)' },
              ...AXIS_GRID,
            },
            xaxis: {
              type: 'date',
              ...AXIS_GRID,
            },
            shapes: [
              {
                type: 'line',
                xref: 'paper',
                x0: 0,
                x1: 1,
                y0: 100,
                y1: 100,
                line: { color: '#666', width: 1, dash: 'dash' },
              },
            ],
          },
        }
      : empty;

  // 2. Asset Class Cumulative NAV
  const navTraces: Data[] = [];
  ASSET_ORDER.forEach((assetClass) => {
    const d = ac[assetClass];
    if (!d?.timeseries?.dates || !d.timeseries.cumulative_nav) return;
    const color = ASSET_COLORS[assetClass] ?? '#666';
    navTraces.push({
      type: 'scatter',
      mode: 'lines',
      name: assetClass,
      x: d.timeseries.dates,
      y: d.timeseries.cumulative_nav,
      line: { color, width: 2 },
      hovertemplate: hoverTemplate(),
    });
  });

  const cumulativeNav: BuiltPlot =
    navTraces.length > 0
      ? {
          data: navTraces,
          layout: {
            // Convention: title at top, legend BETWEEN title and chart.
            ...chartLayout({
              family: 'timeseries',
              marginOverride: { t: 90, b: 70, l: 56, r: 24 },
            }),
            title: { text: 'Asset Class Cumulative NAV' },
            yaxis: {
              title: { text: 'Indexed NAV' },
              ...AXIS_GRID,
            },
            xaxis: {
              type: 'date',
              ...AXIS_GRID,
            },
          },
        }
      : empty;

  return { tactical, cumulativeNav };
}

function cumulativeTitle(data: PerformancePeriodData, hasBenchmark: boolean): string {
  const pct = (value: number | null | undefined): string =>
    value === null || value === undefined ? 'N/A' : `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  const portfolio = pct(data.period_return_pct);
  if (!hasBenchmark) return `Cumulative Performance — Portfolio ${portfolio}`;
  return `Cumulative Performance — Portfolio ${portfolio}  |  Benchmark ${pct(data.benchmark_period_return_pct)}`;
}
